import { useState } from "react";
import { Calendar as CalendarIcon, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn, INPUT_TRIGGER_CLS } from "@/lib/utils";

function toDate(v?: string | null): Date | undefined {
  if (!v) return undefined;
  const [y, m, d] = v.slice(0, 10).split("-").map(Number);
  if (!y || !m || !d) return undefined;
  return new Date(y, m - 1, d);
}

function toYmd(d: Date): string {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

const TIME_SLOTS = Array.from({ length: 48 }, (_, i) => {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${p(Math.floor(i / 2))}:${i % 2 ? "30" : "00"}`;
});

export function DateTimePicker({
  value,
  onChange,
  id,
  disabled,
  placeholder = "Pick date & time",
  className,
}: {
  value: string | null | undefined;
  onChange: (value: string) => void;
  id?: string;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const datePart = value ? value.slice(0, 10) : "";
  const timePart = value && value.length >= 16 ? value.slice(11, 16) : "";
  const selected = toDate(datePart);

  function emit(ymd: string, time: string) {
    if (!ymd) {
      onChange("");
      return;
    }
    onChange(`${ymd}T${time || "00:00"}`);
  }

  const label = selected
    ? `${selected.toLocaleDateString()}${timePart ? ` ${timePart}` : ""}`
    : placeholder;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        id={id}
        disabled={disabled}
        className={cn(INPUT_TRIGGER_CLS, !selected && "text-muted-foreground", className)}
      >
        <span className="truncate text-left">{label}</span>
        <CalendarIcon className="h-4 w-4 shrink-0 opacity-50" />
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <div className="flex">
          <Calendar
            mode="single"
            selected={selected}
            onSelect={(d) => emit(d ? toYmd(d) : "", timePart)}
            autoFocus
          />
          <div className="flex w-36 flex-col border-l">
            <div className="flex flex-col gap-1.5 border-b p-2">
              <span className="flex items-center gap-1.5 px-1 text-xs font-medium uppercase tracking-wide text-slate-600">
                <Clock className="h-3.5 w-3.5" />
                Time
              </span>
              <Input
                type="time"
                value={timePart}
                disabled={!selected}
                onChange={(e) => emit(datePart, e.target.value)}
                className="h-8 tabular-nums [&::-webkit-calendar-picker-indicator]:hidden"
              />
            </div>
            <div className="grid max-h-[13.5rem] grid-cols-1 gap-1 overflow-y-auto p-2">
              {TIME_SLOTS.map((t) => (
                <Button
                  key={t}
                  type="button"
                  size="sm"
                  variant={t === timePart ? "default" : "ghost"}
                  disabled={!selected}
                  onClick={() => emit(datePart, t)}
                  className="h-8 justify-center tabular-nums"
                >
                  {t}
                </Button>
              ))}
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
